import * as React from "react";
import { PageContainer } from "../components/Card";
import { PageHeader } from "../components/PageHeader";
import { Tabs } from "../components/Tabs";
import { CrudPage, CrudResource } from "../components/CrudPage";
import { Badge, StatusBadge } from "../components/Badge";
import { Banner } from "../components/States";
import { useAsync } from "../general/useAsync";
import { usePermission } from "../account/AccountStore";
import { Permissions } from "../account/permissions";
import { listGradeScales } from "../grading/GradeScaleStore";
import * as Store from "./ConfigurationStore";

/**
 * The organization-scoped vocabularies, on one screen with four tabs.
 *
 * Mark types used to be created from inside ProcedureBuilder.tsx, because a chained procedure needs
 * one mark type per level (entered -> division -> term) and there was nowhere else to make them.
 * That put a piece of global configuration inside a per-procedure editor, where it was easy to miss
 * and impossible to review. It lives here now; the procedure editor just picks from the list.
 *
 * All four of these are small lists, so filtering happens client-side and CrudPage handles the rest.
 */

type Tab = "mark-types" | "assessment-types" | "group-types" | "comment-types";

export const ConfigurationIndex: React.FC = () => {
    const [tab, setTab] = React.useState<Tab>("mark-types");

    const canWrite = usePermission(Permissions.CONFIGURATION_WRITE);
    const canManageGradeScales = usePermission(Permissions.GRADE_SCALES_READ);

    // Loaded once for the mark-type grade-scale dropdown. Empty when the user cannot read scales,
    // in which case the field is simply not offered rather than showing an empty required select.
    const gradeScales = useAsync(() => (canManageGradeScales ? listGradeScales() : Promise.resolve([])), [canManageGradeScales], {
        initial: [],
    });

    const gradeScaleOptions = React.useMemo(
        () => [
            { value: "", label: "No default scale" },
            ...(gradeScales.data ?? []).map((s) => ({ value: s.id ?? "", label: s.name ?? "(unnamed)" })),
        ],
        [gradeScales.data]
    );

    const markTypes: CrudResource<Store.MarkTypeModel, { name: string; gradeScaleID: string }> = {
        title: "Mark types",
        icon: ["fad", "tags"],
        description:
            "The kinds of mark that can coexist on the same thing - an entered mark, a division mark, a term mark. A chained procedure needs one per level.",
        singular: "Mark type",
        plural: "Mark types",
        list: Store.listMarkTypes,
        create: (d) => Store.createMarkType({ name: d.name, gradeScaleID: d.gradeScaleID || undefined }),
        update: (id, d) => Store.updateMarkType(id, { name: d.name, gradeScaleID: d.gradeScaleID || undefined }),
        remove: Store.deleteMarkType,
        idOf: (m) => m.id ?? "",
        labelOf: (m) => m.name ?? "",
        columns: [
            { header: "Name", render: (m) => <span className="font-medium text-text-strong">{m.name}</span> },
            {
                header: "Default grade scale",
                render: (m) =>
                    m.gradeScaleName ? <Badge tone="primary">{m.gradeScaleName}</Badge> : <span className="text-text-weak">None</span>,
            },
        ],
        fields: [
            { name: "name", label: "Name", required: true, placeholder: "Term Mark" },
            ...(canManageGradeScales
                ? [
                      {
                          name: "gradeScaleID" as const,
                          label: "Default grade scale",
                          type: "select" as const,
                          options: gradeScaleOptions,
                          hint: "Used to turn a numeric result into a symbol. A procedure version can override it.",
                      },
                  ]
                : []),
        ],
        toDraft: (m) => ({ name: m?.name ?? "", gradeScaleID: m?.gradeScaleID ?? "" }),
        canWrite,
    };

    const assessmentTypes: CrudResource<Store.AssessmentTypeModel, { name: string }> = {
        title: "Assessment types",
        icon: ["fad", "file-pen"],
        description: "What kind of thing an assessment is - Test, Exam, Assignment. Procedure rules can select on these.",
        singular: "Assessment type",
        plural: "Assessment types",
        list: () => Store.listAssessmentTypes(true),
        create: (d) => Store.createAssessmentType({ name: d.name }),
        update: (id, d) => Store.updateAssessmentType(id, { name: d.name }),
        remove: Store.deleteAssessmentType,
        idOf: (t) => t.id ?? "",
        labelOf: (t) => t.name ?? "",
        columns: [
            { header: "Name", render: (t) => <span className="font-medium text-text-strong">{t.name}</span> },
            { header: "Status", render: (t) => <StatusBadge status={t.isArchived ? "Archive" : "Enrolled"} /> },
        ],
        fields: [{ name: "name", label: "Name", required: true, placeholder: "Test" }],
        toDraft: (t) => ({ name: t?.name ?? "" }),
        canWrite,
    };

    const groupTypes: CrudResource<Store.AssessmentTypeModel, { name: string }> = {
        title: "Assessment group types",
        icon: ["fad", "folder-tree"],
        description:
            "The labels for levels of the group tree - Term, Division, Section. Procedure selectors match on the type, never on a particular group, which is what lets one procedure apply across modules.",
        singular: "Group type",
        plural: "Group types",
        list: () => Store.listGroupTypes(true),
        create: (d) => Store.createGroupType({ name: d.name }),
        update: (id, d) => Store.updateGroupType(id, { name: d.name }),
        remove: Store.deleteGroupType,
        idOf: (t) => t.id ?? "",
        labelOf: (t) => t.name ?? "",
        columns: [
            { header: "Name", render: (t) => <span className="font-medium text-text-strong">{t.name}</span> },
            { header: "Status", render: (t) => <StatusBadge status={t.isArchived ? "Archive" : "Enrolled"} /> },
        ],
        fields: [{ name: "name", label: "Name", required: true, placeholder: "Division" }],
        toDraft: (t) => ({ name: t?.name ?? "" }),
        canWrite,
    };

    const commentTypes: CrudResource<Store.CommentTypeModel, { name: string }> = {
        title: "Comment types",
        icon: ["fad", "comment"],
        description: "Free-text remarks that sit alongside marks - the same shape as mark types, but for words instead of numbers.",
        singular: "Comment type",
        plural: "Comment types",
        list: Store.listCommentTypes,
        create: (d) => Store.createCommentType({ name: d.name }),
        update: (id, d) => Store.updateCommentType(id, { name: d.name }),
        remove: Store.deleteCommentType,
        idOf: (t) => t.id ?? "",
        labelOf: (t) => t.name ?? "",
        columns: [{ header: "Name", render: (t) => <span className="font-medium text-text-strong">{t.name}</span> }],
        fields: [{ name: "name", label: "Name", required: true, placeholder: "Teacher remark" }],
        toDraft: (t) => ({ name: t?.name ?? "" }),
        canWrite,
    };

    return (
        <PageContainer>
            <PageHeader
                title="Configuration"
                icon={["fad", "sliders"]}
                description="Vocabularies scoped to this organisation. Everything the assessment structure and the calculation engine name is defined here."
            />

            <Tabs
                active={tab}
                onChange={(k) => setTab(k as Tab)}
                tabs={[
                    { key: "mark-types", label: "Mark types", icon: ["fad", "tags"] },
                    { key: "assessment-types", label: "Assessment types", icon: ["fad", "file-pen"] },
                    { key: "group-types", label: "Group types", icon: ["fad", "folder-tree"] },
                    { key: "comment-types", label: "Comment types", icon: ["fad", "comment"] },
                ]}
            />

            {tab === "mark-types" && (
                <CrudPage variant="section" resource={markTypes} deps={[gradeScales.data]}>
                    {!canManageGradeScales && (
                        <Banner tone="info" className="mb-4">
                            You do not have permission to read grade scales, so the default-scale field is hidden. Mark types still work
                            without one; results simply render as numbers.
                        </Banner>
                    )}
                </CrudPage>
            )}
            {tab === "assessment-types" && <CrudPage variant="section" resource={assessmentTypes} />}
            {tab === "group-types" && <CrudPage variant="section" resource={groupTypes} />}
            {tab === "comment-types" && <CrudPage variant="section" resource={commentTypes} />}
        </PageContainer>
    );
};

export default ConfigurationIndex;
